import React, { ReactNode, useEffect, useState, createContext, useContext, useCallback, useMemo, useRef } from 'react';
import { TenancyProvider, User } from './TenancyContext';
import { Loader2 } from 'lucide-react';
import { firebaseConfig, auth, db, storage, storageRef } from '../js/external-js/config/main_config';
import { getDoc, doc } from 'firebase/firestore';
import { getCompanyImgLink } from '../js/external-js/variableModules/downloadFromSDK.module';
import { getRegisterItems, RegisterItem, loadCachedRegisterItems, saveCachedRegisterItems } from '@/utils/register-items-utils';
import { getKeyDocumentItems, KeyDocumentItem } from '@/utils/key-documents-utils';
import { createIconComponent } from '@/utils/icon-utils';
// Using a different icon approach to avoid module resolution issues
import { FileText } from 'lucide-react';

// Import the Tenancy interface from TenancyContext
import type { Tenancy } from './TenancyContext';

interface TenancyContextProviderProps {
  children: ReactNode;
}

// Add a context for triggering data refreshes and connecting tenancy changes
export const RefreshContext = createContext<{
  refreshUsers: () => void;
  updateActiveTenancy: (tenancy: Tenancy) => void;
}>({
  refreshUsers: () => { },
  updateActiveTenancy: () => { }
});

// Utility functions for caching tenancy data
const TENANCY_CACHE_KEY = 'tenancy_cache_v1';
const ACTIVE_TENANCY_CACHE_KEY = 'active_tenancy_cache_v1';
const USERS_CACHE_KEY = 'users_cache_v1';

function loadCachedTenancies() {
  try {
    const data = sessionStorage.getItem(TENANCY_CACHE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function saveCachedTenancies(tenancies: Tenancy[]) {
  try {
    sessionStorage.setItem(TENANCY_CACHE_KEY, JSON.stringify(tenancies));
  } catch { }
}

function loadCachedActiveTenancy() {
  try {
    const data = sessionStorage.getItem(ACTIVE_TENANCY_CACHE_KEY);
    return data ? JSON.parse(data) : undefined;
  } catch {
    return undefined;
  }
}

function saveCachedActiveTenancy(tenancy: Tenancy | undefined) {
  try {
    if (tenancy) {
      sessionStorage.setItem(ACTIVE_TENANCY_CACHE_KEY, JSON.stringify(tenancy));
    }
  } catch { }
}

// Users list caching utilities
export function loadCachedUsers(tenancyId: string): User[] {
  try {
    const cacheKey = `${USERS_CACHE_KEY}_${tenancyId}`;
    const data = sessionStorage.getItem(cacheKey);
    if (!data) return [];

    const parsed = JSON.parse(data);
    // Check if cache is expired (1 hour)
    const now = new Date().getTime();
    if (now - parsed.timestamp > 60 * 60 * 1000) {
      sessionStorage.removeItem(cacheKey);
      return [];
    }

    return parsed.users;
  } catch (error) {
    console.error('Error loading users from cache:', error);
    return [];
  }
}

export function saveCachedUsers(tenancyId: string, users: User[]) {
  try {
    const cacheKey = `${USERS_CACHE_KEY}_${tenancyId}`;
    const data = {
      users,
      timestamp: new Date().getTime()
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving users to cache:', error);
  }
}

// Register Items Context and Hook

export const RegisterItemsContext = createContext<{
  registerItems: RegisterItem[];
  loading: boolean;
}>({
  registerItems: [],
  loading: true,
});

export function useRegisterItems() {
  return useContext(RegisterItemsContext);
}

// Key Documents Context and Hook

export const KeyDocumentsContext = createContext<{
  keyDocumentItems: KeyDocumentItem[];
  loading: boolean;
}>({
  keyDocumentItems: [],
  loading: true,
});

export function useKeyDocuments() {
  return useContext(KeyDocumentsContext);
}

// Cache keys for key documents
const KEY_DOCUMENTS_CACHE_KEY = 'key_documents_cache_v1';

// Utility functions for caching key documents
export function loadCachedKeyDocuments(tenancyId: string): KeyDocumentItem[] {
  try {
    const cacheKey = `${KEY_DOCUMENTS_CACHE_KEY}_${tenancyId}`;
    const data = sessionStorage.getItem(cacheKey);
    if (!data) return [];

    const parsed = JSON.parse(data);
    // Check if cache is expired (24 hours)
    const now = new Date().getTime();
    if (now - parsed.timestamp > 24 * 60 * 60 * 1000) {
      sessionStorage.removeItem(cacheKey);
      return [];
    }

    const items: KeyDocumentItem[] = [];
    for (const item of parsed.items) {
      items.push({
        ...item,
        icon: createIconComponent((item as any).iconName || 'FileText')
      });
    }
    return items;
  } catch (error) {
    console.error('Error loading key documents from cache:', error);
    return [];
  }
}

export function saveCachedKeyDocuments(tenancyId: string, items: KeyDocumentItem[]) {
  try {
    const cacheKey = `${KEY_DOCUMENTS_CACHE_KEY}_${tenancyId}`;
    const data = {
      items: items.map(item => ({
        ...item,
        icon: (item as any).iconName || item.icon // Store icon name if available, otherwise keep icon
      })),
      timestamp: new Date().getTime()
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving key documents to cache:', error);
  }
}

// Team and Business Unit Interfaces + Context

interface Team {
  id: string;
  name: string;
}

interface BusinessUnit {
  id: string;
  name: string;
}

export const OrgStructureContext = createContext<{
  teams: Team[];
  businessUnits: BusinessUnit[];
  loading: boolean;
}>({
  teams: [],
  businessUnits: [],
  loading: true,
});

export function useOrgStructure() {
  return useContext(OrgStructureContext);
}

// Main Provider

import { getEnabledModules, standardNameToModuleKey } from '@/js/external-js/variableModules/utils/EnabledModulesManager';
import { useResetState } from '@/utils/stateReset';
import { mspService } from '@/services/mspService';

export const TenancyContextProvider: React.FC<TenancyContextProviderProps> = ({ children }) => {
  const [tenancies, setTenancies] = useState<Tenancy[]>([]); // always start empty
  const { registerReset } = useResetState();

  // Register reset function
  useEffect(() => {
    return registerReset(() => {
      setTenancies([]);
      setInitialTenancy(undefined);
      setUsers([]);
      setRegisterItems([]);
      setKeyDocumentItems([]);
      // setOrgStructure({ teams: [], businessUnits: [], loading: false });

      // Clear all caches
      sessionStorage.removeItem(TENANCY_CACHE_KEY);
      sessionStorage.removeItem(ACTIVE_TENANCY_CACHE_KEY);
      sessionStorage.removeItem(USERS_CACHE_KEY);
      sessionStorage.removeItem(KEY_DOCUMENTS_CACHE_KEY);
    });
  }, [registerReset]);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialTenancy, setInitialTenancy] = useState<Tenancy | undefined>(undefined);
  const [hasLoadedFromCache, setHasLoadedFromCache] = useState<boolean>(false);

  const [registerItems, setRegisterItems] = useState<RegisterItem[]>([]);
  const [registerLoading, setRegisterLoading] = useState(true);
  const [keyDocumentItems, setKeyDocumentItems] = useState<KeyDocumentItem[]>([]);
  const [keyDocumentsLoading, setKeyDocumentsLoading] = useState(true);

  // Org structure state
  const [teams, setTeams] = useState<Team[]>([]);
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [orgStructureLoading, setOrgStructureLoading] = useState(true);

  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [hasLoadedUsersFromCache, setHasLoadedUsersFromCache] = useState(false);
  const [usersRefreshCounter, setUsersRefreshCounter] = useState(0);

  // Current user state
  const [currentUser, setCurrentUser] = useState<{ userID: string;[key: string]: any } | undefined>(undefined);

  // Enabled modules state
  // State for enabled modules with proper type
  const [enabledModules, setEnabledModules] = useState<string[]>([]);

  // Fetch enabled modules for the current tenancy
  const fetchEnabledModules = async () => {
    try {
      const modules = await getEnabledModules();
      // Convert Set to array and normalize module keys
      const normalizedModules = Array.from(modules).map(moduleKey => {
        // First try to normalize the key to handle any variations
        const normalizedKey = standardNameToModuleKey(moduleKey) || moduleKey;
        // Format for display (optional, can be removed if raw keys are preferred)
        return normalizedKey;
      });

      setEnabledModules(normalizedModules);
    } catch (error) {
      console.error('[TenancyContextProvider] Error fetching enabled modules:', error);
      setEnabledModules([]);
    }
  };

  // Fetch enabled modules on mount and when activeTenancy changes
  useEffect(() => {
    fetchEnabledModules();
  }, []);

  // On mount, show cached data for instant UI, and fetch fresh in background
  useEffect(() => {
    const cachedTenancies = loadCachedTenancies();
    const cachedActiveTenancy = loadCachedActiveTenancy();
    if (cachedTenancies.length > 0) {
      setTenancies(cachedTenancies);
      setLoading(false); // UI is ready instantly
      setHasLoadedFromCache(true);
    } else {
      setLoading(true); // No cache, show loading
    }
    if (cachedActiveTenancy) {
      setInitialTenancy(cachedActiveTenancy);
      // Enable user cache on initial load so it works on page reload
      setHasLoadedUsersFromCache(true);
    }
  }, []);

  // This effect monitors localStorage changes, but only works across tabs
  // We now have a direct updateActiveTenancy method instead for in-tab updates
  useEffect(() => {
    // Set up a storage event listener to detect tenancy changes from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === ACTIVE_TENANCY_CACHE_KEY) {
        try {
          const newTenancy = JSON.parse(event.newValue || '') as Tenancy | undefined;
          if (newTenancy && newTenancy.id && (!initialTenancy || newTenancy.id !== initialTenancy.id)) {
            setInitialTenancy(newTenancy);
            setHasLoadedUsersFromCache(false);
          }
        } catch (e) {
          console.error('[TenancyContextProvider] Error processing tenancy change:', e);
        }
      }
    };

    // Only add the storage event listener for cross-tab sync
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [initialTenancy]);

  useEffect(() => {
    // Function to fetch tenancies from Firestore
    const fetchTenancies = async () => {
      try {
        // Only set loading if we didn't load from cache
        if (!hasLoadedFromCache) setLoading(true);

        // Get the current user
        const user = auth.currentUser;

        if (!user) {
          setLoading(false);
          return;
        }

        const uid = user.uid;

        const userRef = db.collection('users').doc(uid);
        const userDoc = await userRef.get();

        if (!userDoc.exists) {
          setLoading(false);
          return;
        }

        const userData = userDoc.data();

        if (!userData?.organisations?.length) {
          setLoading(false);
          return;
        }

        // Set current user information
        setCurrentUser({
          userID: uid,
          email: user.email,
          displayName: user.displayName == "" ? userData?.firstName + " " + userData?.lastName : user.displayName,
          photoURL: user.photoURL,
          ...userData,
        });

        const orgsArray = userData.organisations || [];
        const mspOrgsArray = userData.mspOrgs || [];
        const mspImpersonation = userData.mspImpersonation || false;

        // Process user's own organisations in parallel for speed
        const tenancyPromises = orgsArray.map(async (org) => {
          try {
            const orgRef = db.collection('customers').doc(org);
            const orgDoc = await orgRef.get();

            if (orgDoc.exists) {
              const orgData = orgDoc.data();
              const tenancyName = orgData?.customerFriendlyName || 'Unknown Tenancy';
              const legalName = orgData?.customerName || 'Unknown Tenancy';
              const tenancyPlan = orgData?.customerPlan || 'Business Plan';
              const tenancyOwner = orgData?.roles?.companyAdmin ? orgData?.roles?.companyAdmin[0] : orgData?.roles?.customerAdmin ? orgData?.roles?.customerAdmin[0] : 'Unknown';
              const disabledFeatures = orgData?.disabledFeatures || [];
              const isMSPOnly = orgData?.isMSPOnly || false;
              try {
                const logoUrl = await getCompanyImgLink(firebaseConfig, storageRef, org);
                return {
                  name: tenancyName,
                  logo: logoUrl,
                  plan: tenancyPlan,
                  legalName,
                  id: org,
                  tenancyOwner: tenancyOwner,
                  disabledFeatures: disabledFeatures,
                  isMSPOnly: isMSPOnly
                };
              } catch (error) {
                console.error('Error fetching logo for tenancy:', org, error);
                return {
                  name: tenancyName,
                  logo: '/assets/default-tenancy-logo.svg',
                  plan: tenancyPlan,
                  legalName,
                  id: org,
                  tenancyOwner: tenancyOwner,
                  disabledFeatures: disabledFeatures,
                  isMSPOnly: isMSPOnly
                };
              }
            } else {
              return null;
            }
          } catch (error) {
            console.error('[TenancyContextProvider] Error processing organisation:', org, error);
            return null;
          }
        });

        const fetchedTenanciesFromOrgs = (await Promise.all(tenancyPromises)).filter(Boolean);

        const filteredMspOrgs = mspOrgsArray.filter((item) => !orgsArray.includes(item));

        const mspTenancyPromises = filteredMspOrgs.map(async (org) => {
          try {
            const orgRef = db.collection('customers').doc(org);
            const orgDoc = await orgRef.get();

            if (orgDoc.exists) {
              const orgData = orgDoc.data();
              const tenancyName = orgData?.customerFriendlyName || 'Unknown Tenancy';
              const tenancyPlan = orgData?.customerPlan || 'Business Plan';
              const legalName = orgData?.customerName || 'Unknown Tenancy';
              const tenancyOwner = orgData?.customerOwner || 'Unknown';
              const impersonationAllowed = orgData?.mspDetails?.techPartner?.impersonationAllowed || false;
              const impersonationAllowedBusiness = orgData?.mspDetails?.businessPartner?.impersonationAllowed || false;
              const isMSPOnly = orgData?.isMSPOnly || false;

              const defaultLogo = '/assets/default-tenancy-logo.svg';

              try {
                const logoUrl = await getCompanyImgLink(firebaseConfig, storageRef, org);
                return {
                  name: tenancyName,
                  logo: logoUrl,
                  plan: tenancyPlan,
                  legalName,
                  id: org,
                  tenancyOwner,
                  impersonationAllowed,
                  impersonationAllowedBusiness,
                  isMSPOnly: isMSPOnly
                };
              } catch (error) {
                console.error('[TenancyContextProvider] Error fetching logo for MSP tenancy:', org, error);
                return {
                  name: tenancyName,
                  logo: defaultLogo,
                  plan: tenancyPlan,
                  legalName,
                  id: org,
                  tenancyOwner,
                  impersonationAllowed,
                  impersonationAllowedBusiness,
                  isMSPOnly: isMSPOnly
                };
              }
            } else {
              return null;
            }
          } catch (error) {
            console.error('[TenancyContextProvider] Error processing MSP organisation:', org, error);
            return {
              name: `Tenancy ${org.substring(0, 6)}`,
              logo: '/assets/default-tenancy-logo.svg',
              plan: 'Business Plan',
              legalName: `Tenancy ${org.substring(0, 6)}`,
              id: org,
              tenancyOwner: 'Unknown'
            };
          }
        });

        const fetchedMspTenancies = (await Promise.all(mspTenancyPromises)).filter(Boolean);

        const fetchedTenancies: Tenancy[] = [];
        fetchedTenancies.push(...fetchedTenanciesFromOrgs, ...fetchedMspTenancies);

        if (fetchedTenancies.length > 0) {
          setTenancies(fetchedTenancies);
          saveCachedTenancies(fetchedTenancies);

          let initial: Tenancy | undefined = undefined;
          if (mspImpersonation) {
            // For MSP impersonation, only select if there's a specific impersonated tenancy
            initial = fetchedTenancies[1]; // Don't fallback to [0], let user choose
          } else {
            // First try to use cached active tenancy
            const cachedActive = loadCachedActiveTenancy();
            if (cachedActive && fetchedTenancies.find(t => t.id === cachedActive.id)) {
              initial = fetchedTenancies.find(t => t.id === cachedActive.id);
            } else if (userDoc?.primaryTenancy) {
              // Use primary tenancy if set and found
              initial = fetchedTenancies.find(t => t.id === userDoc.primaryTenancy);
            } else if (fetchedTenancies.length === 1 && !fetchedTenancies[0].isMSPOnly) {
              // If user has only one non-MSP tenancy, auto-select it
              initial = fetchedTenancies[0];
            }
            // Don't fallback to the first tenancy if user has multiple tenancies - let user choose
          }
          setInitialTenancy(initial);
          saveCachedActiveTenancy(initial);
          // Only set loading to false after initialTenancy is set
          setTimeout(() => setLoading(false), 0);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('[TenancyContextProvider] Error fetching tenancies:', error);
        setLoading(false);
      }
    };
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchTenancies();
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [hasLoadedFromCache]);

  useEffect(() => {
    if (tenancies.length === 0) return;
    const cachedActive = loadCachedActiveTenancy();
    const found = cachedActive && tenancies.find(t => t.id === cachedActive.id);
    if (found && (!initialTenancy || initialTenancy.id !== found.id)) {
      setInitialTenancy(found);
    }
    // Removed automatic fallback to tenancies[0] - let user choose
  }, [tenancies]);

  useEffect(() => {
    if (initialTenancy) {
      saveCachedActiveTenancy(initialTenancy);
    }
  }, [initialTenancy]);

  useEffect(() => {
    if (tenancies.length > 0) {
      saveCachedTenancies(tenancies);
    }
  }, [tenancies]);

  // Register items load when tenancy changes
  useEffect(() => {
    if (!initialTenancy?.id) {
      setRegisterItems([]);
      setRegisterLoading(false);
      return;
    }

    const cached = loadCachedRegisterItems(initialTenancy.id);
    if (cached.length > 0) {
      setRegisterItems(cached);
      setRegisterLoading(false);
    } else {
      setRegisterLoading(true);
    }

    const fetch = async () => {
      try {
        const isEnterprise = initialTenancy.plan === 'enterprise' || initialTenancy.plan === 'Enterprise Plan';
        const fresh = await getRegisterItems(initialTenancy.id!, { isEnterprise });

        setRegisterItems(fresh);
        saveCachedRegisterItems(initialTenancy.id!, fresh);
      } catch (err) {
        console.error('[TenancyContextProvider] Error fetching register items:', err);
      } finally {
        setRegisterLoading(false);
      }
    };
    fetch();
  }, [initialTenancy]);

  // Key documents load when tenancy changes
  useEffect(() => {
    if (!initialTenancy?.id) {
      setKeyDocumentItems([]);
      setKeyDocumentsLoading(false);
      return;
    }

    const cached = loadCachedKeyDocuments(initialTenancy.id);
    if (cached.length > 0) {
      setKeyDocumentItems(cached);
      setKeyDocumentsLoading(false);
    } else {
      setKeyDocumentsLoading(true);
    }

    const fetch = async () => {
      try {
        const isEnterprise = initialTenancy.plan === 'enterprise' || initialTenancy.plan === 'Enterprise Plan';
        const fresh = await getKeyDocumentItems(initialTenancy.id!, { isEnterprise });
        setKeyDocumentItems(fresh);
        saveCachedKeyDocuments(initialTenancy.id!, fresh);
      } catch (err) {
        console.error('[TenancyContextProvider] Error fetching key document items:', err);
      } finally {
        setKeyDocumentsLoading(false);
      }
    };
    fetch();
  }, [initialTenancy]);

  // Fetch org structure
  useEffect(() => {
    const fetchOrgStructure = async () => {
      if (!initialTenancy?.id) {
        setTeams([]);
        setBusinessUnits([]);
        setOrgStructureLoading(false);
        return;
      }

      try {
        setOrgStructureLoading(true);
        const orgRef = db.collection('customers').doc(initialTenancy.id);
        const orgDoc = await orgRef.get();

        if (!orgDoc.exists) {
          setTeams([]);
          setBusinessUnits([]);
          setOrgStructureLoading(false);
          return;
        }

        const orgData = orgDoc.data();

        // DED-605: Handle both object format {name: string} and string format for team/business unit names
        const teamNames: Team[] = Object.entries(orgData?.customerOrg?.teams?.teamNames || {}).map(
          ([id, value]: [string, any]) => ({
            id,
            name: typeof value === 'string' ? value : (value?.name || id)
          })
        ).filter(t => t.name); // Filter out empty names

        const businessUnitNames: BusinessUnit[] = Object.entries(orgData?.customerOrg?.teams?.businessUnitNames || {}).map(
          ([id, value]: [string, any]) => ({
            id,
            name: typeof value === 'string' ? value : (value?.name || id)
          })
        ).filter(bu => bu.name); // Filter out empty names

        setTeams(teamNames);
        setBusinessUnits(businessUnitNames);
      } catch (err) {
        console.error('[TenancyContextProvider] Error fetching org structure:', err);
        setTeams([]);
        setBusinessUnits([]);
      } finally {
        setOrgStructureLoading(false);
      }
    };

    fetchOrgStructure();
  }, [initialTenancy]);

  // Type for the user data we get from customerOrgUsers
  interface CustomerOrgUserEntry {
    userID: string;
    teamName?: string;
    businessUnit?: string;
    ismsRole?: string;
    ismsCommittee?: string;
    [key: string]: any; // For any additional properties
  }

  // Function to fetch users from Firestore with proper typing and error handling
  const fetchUsers = useCallback(async (
    customerOrgUsers: Record<string, CustomerOrgUserEntry>,
    existingUsers: User[] = []
  ): Promise<User[]> => {
    // Safety check - ensure customerOrgUsers is valid
    if (!customerOrgUsers || typeof customerOrgUsers !== 'object') {
      console.error('[TenancyContextProvider] Invalid customerOrgUsers:', customerOrgUsers);
      return [];
    }

    const entries = Object.entries<CustomerOrgUserEntry>(customerOrgUsers);
    const userMap: Record<string, User> = {};

    // Create a map of existing users by ID for quick lookup
    const existingUserMap = existingUsers.reduce<Record<string, User>>((acc, user) => {
      if (user?.id) {
        acc[user.id] = user;
      }
      return acc;
    }, {});

    // Process users in parallel with error handling for each user
    await Promise.allSettled(
      entries.map(async ([, entry]) => {
        if (!entry?.userID || entry?.userID === '' || entry?.userID === 'UID') return;

        const userId = entry.userID;
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));

          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (!userData) return;

            userMap[userId] = {
              id: userId,
              email: userData.email || '',
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              roles: userData.roles || {},
              businessUnit: entry.businessUnit || '',
              ismsRole: entry.ismsRole || '',
              ismsCommittee: entry.ismsCommittee || '',
            };
          }
        } catch (error) {
          console.error(`[TenancyContextProvider] Error fetching user ${userId}:`, error);
          // If we have cached data for this user, use it as a fallback
          if (existingUserMap[userId]) {
            userMap[userId] = existingUserMap[userId];
          }
        }
      })
    );

    // Second pass: maintain order of existing users and append new ones
    const userList: User[] = [];
    const processedUserIds = new Set<string>();

    // First add existing users in their original order if they still exist
    existingUsers.forEach(user => {
      if (!user?.id) return;

      const freshUser = userMap[user.id];
      if (freshUser) {
        userList.push(freshUser);
        processedUserIds.add(user.id);
      } else if (user) {
        // If we don't have fresh data but have existing user, use it
        userList.push(user);
        processedUserIds.add(user.id);
      }
    });

    // Then add any new users that weren't in the existing list
    Object.entries(userMap).forEach(([userId, user]) => {
      if (user && !processedUserIds.has(userId)) {
        userList.push(user);
      }
    });

    return userList;
  }, []);

  // Track the last tenancy ID we loaded users for
  const lastUsersTenancyId = useRef<string | null>(null);

  // Function to refresh users data with proper error handling and logging
  const refreshUsers = useCallback((): void => {
    // Increment the refresh counter to trigger the effect
    setUsersRefreshCounter(prev => {
      const newCount = prev + 1;
      return newCount;
    });

    // Clear the cache flag to force reload from server
    setHasLoadedUsersFromCache(false);

    // Clear the last tenancy ID to force a full reload
    lastUsersTenancyId.current = null;

    // Clear the cache for the current tenancy if it exists
    const currentTenancyId = initialTenancy?.id;
    if (currentTenancyId) {
      const cacheKey = `users_cache_v1_${currentTenancyId}`;

      try {
        sessionStorage.removeItem(cacheKey);
      } catch (error) {
        console.error('[TenancyContextProvider] Error clearing users cache:', error);
        // Even if cache clearing fails, we can continue with the refresh
      }
    }
  }, [initialTenancy?.id]);

  // Direct function to update the active tenancy in this provider
  const updateActiveTenancy = useCallback(async (tenancy: Tenancy) => {

    // Clear all caches and reset states before switching tenancy
    setUsers([]);
    setRegisterItems([]);
    setKeyDocumentItems([]);
    setTeams([]);
    setBusinessUnits([]);
    setUsersLoading(true);
    setRegisterLoading(true);
    setKeyDocumentsLoading(true);
    setOrgStructureLoading(true);

    // Clear any cached data for the previous tenancy
    if (initialTenancy?.id) {
      sessionStorage.removeItem(`users_cache_v1_${initialTenancy.id}`);
      sessionStorage.removeItem(`register_items_${initialTenancy.id}`);
      sessionStorage.removeItem(`${KEY_DOCUMENTS_CACHE_KEY}_${initialTenancy.id}`);
    }

    // Update the active tenancy
    setInitialTenancy(tenancy);
    saveCachedActiveTenancy(tenancy);

    // Force reload all data for the new tenancy
    setHasLoadedUsersFromCache(false);
    setHasLoadedFromCache(false);

    // Fetch fresh data for the new tenancy
    try {
      // Force refresh of enabled modules
      await fetchEnabledModules();

      // Force refresh of register items
      const isEnterprise = tenancy.plan === 'enterprise' || tenancy.plan === 'Enterprise Plan';
      const freshRegisterItems = await getRegisterItems(tenancy.id, { isEnterprise });
      setRegisterItems(freshRegisterItems);
      saveCachedRegisterItems(tenancy.id, freshRegisterItems);

      // Force refresh of key documents
      const freshKeyDocuments = await getKeyDocumentItems(tenancy.id, { isEnterprise });
      setKeyDocumentItems(freshKeyDocuments);
      saveCachedKeyDocuments(tenancy.id, freshKeyDocuments);

      // Force refresh of org structure
      const orgRef = db.collection('customers').doc(tenancy.id);
      const orgDoc = await orgRef.get();
      if (orgDoc.exists) {
        const orgData = orgDoc.data();
        const teamNames: Team[] = Object.entries(orgData?.customerOrg?.teams?.teamNames || {}).map(
          ([id, value]: [string, any]) => ({
            id,
            name: value.name
          })
        );
        const businessUnitNames: BusinessUnit[] = Object.entries(orgData?.customerOrg?.teams?.businessUnitNames || {}).map(
          ([id, value]: [string, any]) => ({
            id,
            name: value.name
          })
        );
        setTeams(teamNames);
        setBusinessUnits(businessUnitNames);
      }
    } catch (error) {
      console.error('[TenancyContextProvider] Error refreshing data for new tenancy:', error);
    } finally {
      setRegisterLoading(false);
      setKeyDocumentsLoading(false);
      setOrgStructureLoading(false);
    }
  }, [initialTenancy]);

  // RefreshContext value
  const refreshContextValue = useMemo(() => ({
    refreshUsers,
    updateActiveTenancy
  }), [refreshUsers, updateActiveTenancy]);

  // Load users when tenancy changes or refresh is triggered
  useEffect(() => {
    if (!initialTenancy?.id) {
      setUsers([]);
      setUsersLoading(false);
      return;
    }

    // Track tenancy changes or refresh triggers

    // Reset state when tenancy changes or refresh is triggered
    setUsersLoading(true);

    // Check if user is authenticated
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error('[TenancyContextProvider] No authenticated user, cannot fetch users');
      setUsers([]);
      setUsersLoading(false);
      return;
    }

    // Only use cache if we haven't just called refreshUsers
    // and if we're not switching tenancies (which also calls refreshUsers)
    const cachedUsers = loadCachedUsers(initialTenancy.id);
    if (cachedUsers.length > 0 && hasLoadedUsersFromCache) {
      setUsers(cachedUsers);
      setUsersLoading(false);
    }

    // Set up real-time listener with error handling
    const unsub = db.collection('customers').doc(initialTenancy.id).onSnapshot(
      async (snapshot) => {
        if (!snapshot.exists) {
          console.error('[TenancyContextProvider] Customer document does not exist or cannot be accessed:', initialTenancy.id);
          setUsers([]);
          saveCachedUsers(initialTenancy.id, []);
          setUsersLoading(false);
          return;
        }

        const data = snapshot.data();
        if (!data?.customerOrg?.users) {
          setUsers([]);
          saveCachedUsers(initialTenancy.id, []);
          setUsersLoading(false);
          return;
        }

        // Only show loading indicator if we don't have cached data
        if (!hasLoadedUsersFromCache) {
          setUsersLoading(true);
        }

        try {
          // Make sure we have valid data before proceeding
          if (data && data.customerOrg && data.customerOrg.users) {
            // Pass current users to maintain order
            let userList = await fetchUsers(data.customerOrg.users, users);

            // MSP-as-user logic
            let techPartnerAdded = false;
            let businessPartnerAdded = false;

            // Helper function to add MSP user
            const addMspUser = (mspId: string, type: 'tech' | 'business') => {
              return new Promise<void>(async (resolve) => {
                try {
                  const mspDetails = await mspService.getMspDetails(mspId, initialTenancy.id);
                  const mspUserId = `msp:${mspId}`;

                  // Only add if not already in list
                  if (!userList.some(u => u.id === mspUserId)) {
                    userList.push({
                      id: mspUserId,
                      email: '',
                      firstName: mspDetails.customerFriendlyName,
                      lastName: `(${type === 'tech' ? 'Tech' : 'Business'} MSP)`,
                      roles: { MSP: true },
                      businessUnit: '',
                      ismsRole: '',
                      ismsCommittee: ''
                    });

                    if (type === 'tech') {
                      techPartnerAdded = true;
                    } else {
                      businessPartnerAdded = true;
                    }
                  }
                } catch (error) {
                  console.error(`[TenancyContextProvider] Failed to fetch ${type} MSP details:`, error);
                } finally {
                  resolve();
                }
              });
            };

            // Process tech partner if exists
            if (data.mspDetails?.techPartner && data.mspDetails.techPartner.msp && !techPartnerAdded) {
              await addMspUser(data.mspDetails.techPartner.msp, 'tech');
            }

            // Process business partner if exists and different from tech partner
            if (
              data.mspDetails?.businessPartner && data.mspDetails.businessPartner.msp &&
              !businessPartnerAdded &&
              data.mspDetails.businessPartner.msp !== data.mspDetails.techPartner.msp
            ) {
              await addMspUser(data.mspDetails.businessPartner.msp, 'business');
            }

            setUsers(userList);
            // Update cache with fresh data
            saveCachedUsers(initialTenancy.id, userList);
          } else {
            // Missing data structure
          }

          setUsersLoading(false);
        } catch (error) {
          console.error('[TenancyContextProvider] Error fetching users:', error);
          setUsersLoading(false);
        }
      },
      (error) => {
        console.error('[TenancyContextProvider] Snapshot error:', {
          code: error.code,
          message: error.message,
          name: error.name,
          details: error,
          tenancyId: initialTenancy.id,
          currentUser: auth.currentUser?.uid
        });

        // If permission denied, clear any cached data
        if (error.code === 'permission-denied') {
          console.error('[TenancyContextProvider] Permission denied. Current user may not have access to this tenancy.');
          setUsers([]);
          saveCachedUsers(initialTenancy.id, []);
        }

        setUsersLoading(false);
      });

    return () => unsub();
  }, [initialTenancy, fetchUsers, hasLoadedUsersFromCache, usersRefreshCounter]);

  // ... (rest of the code remains the same)
  const userIdToName = useMemo(() => {
    const mapping: Record<string, string> = {};
    users.forEach(user => {
      if (user.id && (user.firstName || user.lastName)) {
        mapping[user.id] = [user.firstName, user.lastName].filter(Boolean).join(' ');
      } else if (user.id && user.email) {
        mapping[user.id] = user.email;
      }
    });
    return mapping;
  }, [users]);

  // Create a mapping of tenancy IDs to names
  const tenancyIdToName = useMemo(() => {
    const mapping: Record<string, string> = {};
    tenancies.forEach(tenancy => {
      if (tenancy.id) {
        mapping[tenancy.id] = tenancy.name || 'Unnamed Tenancy';
      }
    });
    return mapping;
  }, [tenancies]);

  // Check if a module is enabled
  // Check if a module is enabled
  const isModuleEnabled = useCallback((moduleName: string, dbName?: string): boolean => {
    if (!moduleName || typeof moduleName !== 'string') return false;

    try {
      // Handle case where standardNameToModuleKey might not be available
      const normalizedInput = dbName ? dbName : (standardNameToModuleKey ? standardNameToModuleKey(moduleName) : moduleName).toLowerCase();

      // Safely map over enabledModules
      const normalizedEnabledModules = (enabledModules || []).map(m =>
        m ? m.toLowerCase() : ''
      );

      // Check if the normalized module name is in the enabled modules
      return normalizedEnabledModules.some(module =>
        module && (
          module === normalizedInput ||
          module.includes(normalizedInput) ||
          normalizedInput.includes(module)
        )
      );
    } catch (error) {
      console.error('[TenancyContextProvider] Error checking module:', moduleName, error);
      return false;
    }
  }, [enabledModules, standardNameToModuleKey]);

  // Return the provider tree
  return (
    <TenancyProvider
      initialTenancy={initialTenancy}
      tenancies={tenancies}
      loading={loading}
      users={users}
      usersLoading={usersLoading}
      currentUser={currentUser}
      enabledModules={enabledModules}
      isModuleEnabled={isModuleEnabled}
    >
      <RegisterItemsContext.Provider value={{ registerItems, loading: registerLoading }}>
        <KeyDocumentsContext.Provider value={{ keyDocumentItems, loading: keyDocumentsLoading }}>
          <OrgStructureContext.Provider value={{ teams, businessUnits, loading: orgStructureLoading }}>
            <RefreshContext.Provider value={refreshContextValue}>
              {children}
            </RefreshContext.Provider>
          </OrgStructureContext.Provider>
        </KeyDocumentsContext.Provider>
      </RegisterItemsContext.Provider>
    </TenancyProvider>
  );
}
